import { TimeSeriesChartDataItem } from 'utils/components/TimeSeriesChart/TimeSeriesChartData';
import { DateTime } from 'luxon';
import {
    applyShowDataFor,
    ShowDataFor,
} from 'utils/components/ShowDataFor/ShowDataFor';
import isNil from 'lodash/isNil';
import {
    mapFromElementRangeArray,
    TimeSeriesDisableRange,
} from 'utils/timeseries/TimeSeriesDisableRange';
import { v4 as uuid } from 'uuid';
import { BucketedDataItem } from 'utils/timeseries/bucket';
import {
    PriceStreamDataState,
    PriceTimeDataTimeSeriesItem,
} from 'widgets/candlestickTile/store/stream/PriceStreamState';
import { timeUtils } from '@instinet-analytics/core-lib';
import { MarketDataBarItem } from 'marketData/store/MarketDataState';
import { KeyedStreamItem } from 'communication/types/StreamElement';

const { mapEpochToTime } = timeUtils;

export interface CandlestickTileData {
    breaksInTrading: TimeSeriesDisableRange[];
    timeSeries: CandlestickTileDataTimeSeriesItem[];
    startTime: number;
    endTime: number;
    id: string;
    tickSize?: number;
}

export interface CandlestickTileDataTimeSeriesItem
    extends PriceTimeDataTimeSeriesItem,
        TimeSeriesChartDataItem,
        BucketedDataItem {}

export function buildCandlestickTileData(
    bucketSize: number,
    streamData: PriceStreamDataState | undefined,
    externalMarketBarData: MarketDataBarItem[] | undefined,
    showDataFor?: ShowDataFor
): CandlestickTileData | undefined {
    const timeSeries = externalMarketBarData
        ? buildTimeSeriesFromExternalMarketData(externalMarketBarData)
        : buildTimeSeriesFromIndigoStream(streamData);

    if (!timeSeries || timeSeries.length === 0) {
        return undefined;
    }

    const breaksInTrading = mapFromElementRangeArray(streamData?.disableRange);
    const tickSize = streamData?.orderStat?.tickSize;

    return {
        id: uuid(),
        breaksInTrading,
        timeSeries,
        tickSize,
        startTime: timeSeries[0].startTime,
        endTime: timeSeries[timeSeries.length - 1].endTime,
    };

    function buildTimeSeriesFromIndigoStream(
        streamData: PriceStreamDataState | undefined
    ): CandlestickTileDataTimeSeriesItem[] | undefined {
        if (!streamData?.timeSeries?.length) {
            return undefined;
        }

        const filteredTimeSeries = streamData.timeSeries.filter(
            (t) => !isNil(t.high)
        );

        const sorted = filteredTimeSeries.sort((a, b) =>
            a._i > b._i ? +1 : -1
        );
        const truncatedTimeSeries = applyShowDataFor(sorted, showDataFor);
        return truncatedTimeSeries.map(mapTimeSeriesItem);
    }

    function buildTimeSeriesFromExternalMarketData(
        externalMarketBarData: MarketDataBarItem[]
    ): CandlestickTileDataTimeSeriesItem[] | undefined {
        const sorted = externalMarketBarData.sort((a, b) =>
            a.time > b.time ? +1 : -1
        );
        const keyed: Array<MarketDataBarItem & KeyedStreamItem> = sorted.map(
            (s) => ({ ...s, _i: s.time / 60000 })
        );
        const truncatedTimeSeries = applyShowDataFor(keyed, showDataFor);

        return truncatedTimeSeries.map(mapExternalMarketDataItem);
    }

    function mapExternalMarketDataItem(
        item: MarketDataBarItem & KeyedStreamItem
    ): CandlestickTileDataTimeSeriesItem {
        const startTime = item.time;
        const time = DateTime.fromMillis(startTime).plus({
            minutes: bucketSize / 2,
        });
        const endTime = time.plus({
            minutes: bucketSize / 2,
        });

        return {
            _i: item._i,
            startTime,
            time: time.toMillis(),
            endTime: endTime.toMillis(),
            midTime: time.toMillis(),
            endTimeDisplay: endTime.toMillis(),
            open: item.open ?? 0,
            close: item.close ?? 0,
            high: item.high ?? 0,
            low: item.low ?? 0,
            volume: item.volume ?? 0,
        };
    }

    function mapTimeSeriesItem(
        item: PriceTimeDataTimeSeriesItem
    ): CandlestickTileDataTimeSeriesItem {
        const startTime = mapEpochToTime(item._i);
        const time = DateTime.fromMillis(startTime).plus({
            minutes: bucketSize / 2,
        });
        const endTime = time.plus({
            minutes: bucketSize / 2,
        });

        return {
            ...item,
            startTime,
            time: time.toMillis(),
            endTime: endTime.toMillis(),
            midTime: time.toMillis(),
            endTimeDisplay: endTime.toMillis(),
        };
    }
}
